import { z } from 'zod';
import { nonEmptyPatch } from './_patch.js';

const trimmedNonEmpty = z
  .string()
  .min(1)
  .transform((s) => s.trim())
  .refine((s) => s.length > 0, 'must not be blank');

// WHY (#699): short-name display abbreviation is restricted to 1–3
// characters drawn from Cyrillic letters, Latin letters, or digits.
// Mirrors the table column type (`TEXT NOT NULL`) plus the
// `Москв` rejection test in the Playwright e2e (5 chars, exceeds the
// 3-char ceiling). Trimming happens before the regex check so leading
// or trailing whitespace cannot smuggle a 4th visible character past
// the format gate.
const SHORT_NAME_REGEX = /^[А-Яа-яA-Za-z0-9]{1,3}$/;
const arrivalCityShortName = z
  .string()
  .min(1)
  .max(16) // cheap pre-trim ceiling; the regex below is the real gate
  .transform((s) => s.trim())
  .refine((s) => SHORT_NAME_REGEX.test(s), {
    message: 'shortName must be 1-3 Cyrillic/Latin letters or digits',
  });

/**
 * @aiPurpose canonical wire shape for an Arrival City row. The
 *   directory holds city names only — no country, region, route,
 *   warehouse, port, station, volume, description, or request-link
 *   fields (#623 decisions §17, §26). #699 adds two business fields:
 *   `shortName` (display abbreviation) and `isDefault` (single-row
 *   default-selection flag).
 * @aiInvariant `name` is trimmed and rejected when blank. DB enforces
 *   active-only normalized uniqueness via partial unique indexes on
 *   `lower(name) WHERE active = true` AND `lower(shortName) WHERE
 *   active = true` (declared in the corresponding migrations);
 *   restoring an archived duplicate while an active duplicate exists
 *   surfaces `DOMAIN_CONFLICT`. `isDefault = true` is single-row
 *   globally; the partial unique index `isDefault WHERE isDefault =
 *   true` rejects the second-true row at the DB level, and the
 *   service-layer write guard atomically clears any prior default in
 *   the same transaction so callers receive a typed
 *   `ArrivalCityDefaultConflictError` instead of a raw P2002 on the
 *   happy path.
 * @aiSecurity none — reference data, no PII surface. City names and
 *   short codes are not treated as sensitive (#623 Audit section).
 */
export const ArrivalCitySchema = z
  .object({
    id: z.string().uuid(),
    name: trimmedNonEmpty,
    shortName: arrivalCityShortName,
    isDefault: z.boolean(),
    active: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();
export type ArrivalCity = z.infer<typeof ArrivalCitySchema>;

/**
 * @aiPurpose `POST /api/arrival-cities` body. Business fields are
 *   `name`, `shortName` (required), and `isDefault` (optional, defaults
 *   to `false`); `active` defaults to `true`.
 */
export const CreateArrivalCityInputSchema = z
  .object({
    name: trimmedNonEmpty,
    shortName: arrivalCityShortName,
    isDefault: z.boolean().default(false),
    active: z.boolean().default(true),
  })
  .strict();
export type CreateArrivalCityInput = z.infer<typeof CreateArrivalCityInputSchema>;

/**
 * @aiPurpose `PATCH /api/arrival-cities/:id` body. `active` is reserved
 *   for the dedicated archive/activate endpoints; the patch shape
 *   intentionally only exposes business fields so the lifecycle ledger
 *   (`arrivalCity.archive` / `arrivalCity.restore`) never collides
 *   with field updates audited as `arrivalCity.update`. Setting
 *   `isDefault: true` triggers the service-layer guard that clears the
 *   previous default inside the same transaction.
 */
export const PatchArrivalCityInputSchema = nonEmptyPatch(
  z
    .object({
      name: trimmedNonEmpty,
      shortName: arrivalCityShortName,
      isDefault: z.boolean(),
    })
    .partial()
    .strict(),
);
export type PatchArrivalCityInput = z.infer<typeof PatchArrivalCityInputSchema>;

const stringBoolean = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .transform((v) => (typeof v === 'boolean' ? v : v === 'true'));

/**
 * @aiPurpose query string for `GET /api/arrival-cities`. `q` matches
 *   normalized `name` with case-insensitive substring semantics (#623
 *   decision §39).
 * @aiInvariant `.strict()` rejects unknown keys. `limit` defaults to
 *   `50`, max `500` (#623 decision §40 cap raised in #875 to fit the
 *   single-round-trip active-cities dropdown). `active` defaults to `true`
 *   so pickers omit archived rows by default. List responses are
 *   sorted by normalized city name ASC (#623 decision §36); no
 *   `sortOrder` field exists in this issue.
 */
export const GetArrivalCitiesQuerySchema = z
  .object({
    q: z.string().min(1).max(200).optional(),
    active: stringBoolean.optional(),
    cursor: z.string().min(1).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(50),
  })
  .strict();
export type GetArrivalCitiesQuery = z.infer<typeof GetArrivalCitiesQuerySchema>;

/**
 * @aiPurpose response envelope for `GET /api/arrival-cities`.
 * @aiInvariant `nextCursor` is `null` exactly when the underlying
 *   query returned ≤ `limit` rows; otherwise it carries the encoded
 *   `(name_lower, id)` of the last returned row.
 */
export const GetArrivalCitiesResponseSchema = z
  .object({
    items: z.array(ArrivalCitySchema),
    nextCursor: z.string().min(1).nullable(),
  })
  .strict();
export type GetArrivalCitiesResponse = z.infer<typeof GetArrivalCitiesResponseSchema>;
